import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import "@/App.css";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import POS from "@/pages/POS";
import Products from "@/pages/Products";
import Stock from "@/pages/Stock";
import Purchases from "@/pages/Purchases";
import Sales from "@/pages/Sales";
import Reports from "@/pages/Reports";
import Reconciliation from "@/pages/Reconciliation";
import Outlets from "@/pages/Outlets";
import Users from "@/pages/Users";
import RolePermissions from "@/pages/RolePermissions";
import AuditLog from "@/pages/AuditLog";
import Subscription from "@/pages/Subscription";
import Promos from "@/pages/Promos";
import QrisSettings from "@/pages/QrisSettings";
import PlatformDashboard from "@/pages/admin/PlatformDashboard";
import Mitras from "@/pages/admin/Mitras";
import Plans from "@/pages/admin/Plans";
import PlatformQris from "@/pages/admin/PlatformQris";
import SubscriptionPayments from "@/pages/admin/SubscriptionPayments";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-8 text-sm text-zinc-500">Memuat…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PlatformOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-8 text-sm text-zinc-500">Memuat…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "platform_owner") return <Navigate to="/app" replace />;
  return children;
}

function FeatureOnly({ feature, children }) {
  const { user, loading, hasFeature } = useAuth();
  if (loading) return <div className="p-8 text-sm text-zinc-500">Memuat…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!hasFeature(feature)) return <Navigate to="/app/subscription" replace />;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />

          <Route path="/app" element={<Protected><Layout /></Protected>}>
            <Route index element={<Dashboard />} />
            <Route path="pos" element={<FeatureOnly feature="pos"><POS /></FeatureOnly>} />
            <Route path="sales" element={<FeatureOnly feature="sales_history"><Sales /></FeatureOnly>} />
            <Route path="products" element={<FeatureOnly feature="product_management"><Products /></FeatureOnly>} />
            <Route path="stock" element={<FeatureOnly feature="stock_management"><Stock /></FeatureOnly>} />
            <Route path="purchases" element={<FeatureOnly feature="purchases"><Purchases /></FeatureOnly>} />
            <Route path="reports" element={<FeatureOnly feature="reports"><Reports /></FeatureOnly>} />
            <Route path="reconciliation" element={<FeatureOnly feature="reconciliation"><Reconciliation /></FeatureOnly>} />
            <Route path="outlets" element={<Outlets />} />
            <Route path="users" element={<FeatureOnly feature="team_management"><Users /></FeatureOnly>} />
            <Route path="role-permissions" element={<FeatureOnly feature="team_management"><RolePermissions /></FeatureOnly>} />
            <Route path="audit" element={<FeatureOnly feature="audit_logs"><AuditLog /></FeatureOnly>} />
            <Route path="subscription" element={<Subscription />} />
            <Route path="promos" element={<FeatureOnly feature="promos"><Promos /></FeatureOnly>} />
            <Route path="qris" element={<FeatureOnly feature="qris"><QrisSettings /></FeatureOnly>} />
          </Route>

          <Route path="/platform" element={<PlatformOnly><Layout platform /></PlatformOnly>}>
            <Route index element={<PlatformDashboard />} />
            <Route path="mitras" element={<Mitras />} />
            <Route path="plans" element={<Plans />} />
            <Route path="qris" element={<PlatformQris />} />
            <Route path="payments" element={<SubscriptionPayments />} />
          </Route>

          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}
